import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';

type RemoteLog = {
  enabled?: boolean;
  destination?: string;
  protocol?: 'udp' | 'tcp';
  port?: number;
  mark?: boolean;
  interval?: number;
};

export default function WebUiSettingsRemoteLoggingPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [form, setForm] = React.useState<RemoteLog>({});
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const data = await postWebUiApi<RemoteLog>({
      service: 'log',
      access: 'get',
      command: 'get remote',
    });
    setForm(data);
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Settings', 'Remote Logging']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex flex-col gap-2">
        <table className="webui-table min-w-96 max-w-fit">
          <thead>
            <tr className="webui-table-row-head">
              <th>Setting</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="webui-table-row-content">
              <td>Enabled</td>
              <td className="text-center">
                <WebUiInput
                  type="checkbox"
                  disabled={readonly}
                  checked={Boolean(form.enabled)}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, enabled: e.target.checked }))
                  }
                />
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Destination</td>
              <td>
                <WebUiInput
                  inputSize="sm"
                  className="w-full"
                  disabled={readonly}
                  value={form.destination ?? ''}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, destination: e.target.value }))
                  }
                />
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Protocol</td>
              <td>
                <WebUiSelect
                  inputSize="sm"
                  className="w-full"
                  options={[
                    { label: 'UDP', value: 'udp' },
                    { label: 'TCP', value: 'tcp' },
                  ]}
                  disabled={readonly}
                  value={form.protocol ?? 'udp'}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      protocol: e.target.value as 'udp' | 'tcp',
                    }))
                  }
                />
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Port</td>
              <td>
                <WebUiInput
                  inputSize="sm"
                  className="w-full"
                  disabled={readonly}
                  value={form.port ?? 514}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, port: Number(e.target.value) }))
                  }
                />
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Periodic heartbeat messages</td>
              <td>
                <WebUiInput
                  inputSize="sm"
                  className="w-full"
                  type="checkbox"
                  disabled={readonly}
                  checked={Boolean(form.mark)}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, mark: e.target.checked }))
                  }
                />
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Interval of heartbeat messages (seconds)</td>
              <td>
                <WebUiInput
                  inputSize="sm"
                  className="w-full"
                  type="number"
                  disabled={readonly}
                  value={form.interval ?? 60}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, interval: Number(e.target.value) }))
                  }
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <WebUiButton
            disabled={readonly}
            onClick={async () => {
              try {
                await postWebUiApi({
                  service: 'log',
                  access: 'set',
                  command: 'set remote',
                  parameters: form,
                });
                setMessage('Remote logging updated.');
                setError(null);
                await reload();
              } catch (err) {
                setError((err as Error).message);
              }
            }}
          >
            Set Remote Logging
          </WebUiButton>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
